// SPDX-FileCopyrightText: 2026 Sequent Tech Inc <legal@sequentech.io>
//
// SPDX-License-Identifier: AGPL-3.0-only
var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
// What the Approvals queue and the review of one enrollment say about an
// application: who applied, what happened to it and how it compares with a
// voter of the registry.
import { IApplicationsStatus } from "@/types/applications";
import { EFieldMatch, EIdentityMethod, readConditions } from "./approvalMatrix";
var KEY = "approvalsScreen";
/** Identity documents whose first and middle name are compared together. */
var JOINT_NAME_DOCUMENTS = ["seamanBook", "driversLicense"];
var ID_CARD_TYPE_FIELD = "sequent.read-only.id-card-type";
var FIRST_NAME = "firstName";
var MIDDLE_NAME = "middleName";
/** The voter attributes the registry lookup filters by name instead of as an attribute. */
var VOTER_COLUMNS = {
    firstName: "first_name",
    lastName: "last_name",
    email: "email",
    username: "username",
};
var isRecord = function (value) {
    return typeof value === "object" && value !== null && !Array.isArray(value);
};
var text = function (value) {
    return typeof value === "string" ? value.trim() : typeof value === "number" ? String(value) : "";
};
/** The applicant's answers by attribute name. */
export function applicantData(application) {
    var data = application.applicant_data;
    if (!isRecord(data)) {
        return {};
    }
    return Object.fromEntries(Object.entries(data).map(function (_a) {
        var key = _a[0], value = _a[1];
        return [key, text(value)];
    }));
}
var annotations = function (application) {
    return isRecord(application.annotations) ? application.annotations : {};
};
/** "Juan Carlos Dela Cruz", from the names on the enrollment. */
export function applicantName(application) {
    var data = applicantData(application);
    return [data.firstName, data.middleName, data.lastName].filter(function (part) { return part; }).join(" ");
}
/** "JC" for "Juan Carlos Dela Cruz": the first letters of the first and last word. */
export function initials(name) {
    var words = name.split(/\s+/).filter(function (word) { return word; });
    if (words.length === 0) {
        return "";
    }
    var letters = words.length === 1 ? [words[0]] : [words[0], words[words.length - 1]];
    return letters.map(function (word) { return word.charAt(0).toUpperCase(); }).join("");
}
/** A comma separated annotation as a list. */
export function listAnnotation(application, name) {
    var value = annotations(application)[name];
    return typeof value === "string"
        ? value
            .split(",")
            .map(function (item) { return item.trim(); })
            .filter(function (item) { return item; })
        : [];
}
/** The decision record of an application; empty when it has none. */
export function decisionDetails(application) {
    var record = annotations(application).decision;
    if (!isRecord(record) || typeof record.matrix_version !== "number") {
        return null;
    }
    var inputs = isRecord(record.inputs) ? record.inputs : {};
    var fields = {};
    Object.entries(isRecord(inputs.fields) ? inputs.fields : {}).forEach(function (_a) {
        var field = _a[0], result = _a[1];
        if (result === EFieldMatch.MATCHES || result === EFieldMatch.DIFFERS) {
            fields[field] = result;
        }
    });
    return {
        matrixVersion: record.matrix_version,
        rule: typeof record.rule === "number" ? record.rule : null,
        conditions: readConditions(record.conditions),
        identity: inputs.identity === EIdentityMethod.VERIFIED ||
            inputs.identity === EIdentityMethod.MANUAL_ENTRY
            ? inputs.identity
            : null,
        voterFound: inputs.voter_found === true,
        alreadyEnrolled: inputs.already_enrolled === true,
        approvedVoters: typeof record.accepted_candidates === "number" ? record.accepted_candidates : 0,
        fields: fields,
    };
}
/** The compared fields of the decision that differ from the registry. */
export var differingFields = function (decision) {
    return Object.entries(decision.fields)
        .filter(function (_a) {
        var result = _a[1];
        return result === EFieldMatch.DIFFERS;
    })
        .map(function (_a) {
        var field = _a[0];
        return field;
    });
};
/** "first name, last name and embassy". */
export function joinList(items, t) {
    if (items.length < 2) {
        return items.join("");
    }
    return t("".concat(KEY, ".summary.join"), {
        head: items.slice(0, -1).join(", "),
        last: items[items.length - 1],
    });
}
var capitalize = function (value) { return value.charAt(0).toUpperCase() + value.slice(1); };
/** The officer who decided the application by hand; empty when the rules did. */
export function decidedBy(application) {
    return text(annotations(application).verified_by);
}
/**
 * The two lines of the queue's "What happened" column: what the rules found,
 * and how the identity was established or who decided.
 */
export function enrollmentSummary(application, t, fieldLabel) {
    var decision = decisionDetails(application);
    var officer = decidedBy(application);
    var manual = (decision === null || decision === void 0 ? void 0 : decision.identity) === EIdentityMethod.MANUAL_ENTRY;
    var differing = decision && decision.voterFound ? differingFields(decision) : [];
    var found = differing.length > 0
        ? capitalize(t("".concat(KEY, ".summary.differs"), {
            count: differing.length,
            fields: joinList(differing.map(function (field) { return fieldLabel(field).toLowerCase(); }), t),
        }))
        : decision && !decision.voterFound
            ? t("".concat(KEY, ".summary.noVoter"))
            : decision
                ? t("".concat(KEY, ".summary.allMatch"))
                : "";
    var identity = manual
        ? t("".concat(KEY, ".summary.needsFaceToFace"))
        : (decision === null || decision === void 0 ? void 0 : decision.identity) === EIdentityMethod.VERIFIED
            ? t("".concat(KEY, ".summary.scanVerified"))
            : "";
    if (application.status === IApplicationsStatus.ACCEPTED) {
        return {
            headline: officer
                ? t("".concat(KEY, ".summary.approvedBy"), { name: officer })
                : t("".concat(KEY, ".summary.approvedAuto")),
            detail: found,
        };
    }
    if (application.status === IApplicationsStatus.REJECTED) {
        return {
            headline: officer
                ? t("".concat(KEY, ".summary.rejectedBy"), { name: officer })
                : t("".concat(KEY, ".summary.rejectedAuto")),
            detail: found,
        };
    }
    if (manual) {
        return { headline: t("".concat(KEY, ".summary.typedByHand")), detail: identity };
    }
    return { headline: found || t("".concat(KEY, ".summary.needsReview")), detail: identity };
}
var MINUTE = 60 * 1000;
var HOUR = 60 * MINUTE;
var DAY = 24 * HOUR;
/** "3 hours", "9 days": how long ago the enrollment was sent. */
export function waitingTime(createdAt, now, t) {
    var sent = createdAt ? new Date(createdAt).getTime() : NaN;
    if (Number.isNaN(sent)) {
        return "";
    }
    var elapsed = Math.max(0, now.getTime() - sent);
    if (elapsed >= DAY) {
        return t("".concat(KEY, ".time.days"), { count: Math.floor(elapsed / DAY) });
    }
    if (elapsed >= HOUR) {
        return t("".concat(KEY, ".time.hours"), { count: Math.floor(elapsed / HOUR) });
    }
    return t("".concat(KEY, ".time.minutes"), { count: Math.max(1, Math.floor(elapsed / MINUTE)) });
}
/** Lower case, without accents, hyphens or periods, as the rules compare names. */
export var comparable = function (value) {
    return value
        .toLowerCase()
        .replace(/-/g, " ")
        .replace(/\./g, "")
        .normalize("NFD")
        .replace(/[̀-ͯ]/g, "")
        .replace(/\s+/g, " ")
        .trim();
};
export var sameValue = function (enrollment, registry) {
    return comparable(enrollment) === comparable(registry);
};
/** A registry voter's value of a compared field. */
export function voterValue(voter, field) {
    var _a;
    var column = VOTER_COLUMNS[field];
    if (column) {
        return text(voter[column]);
    }
    var value = (_a = voter.attributes) === null || _a === void 0 ? void 0 : _a[field];
    return text(Array.isArray(value) ? value[0] : value);
}
/**
 * Compares the enrollment with a registry voter on the compared fields, the
 * way the rules do: first and middle name together for the identity documents
 * that print them together.
 */
export function compareWithVoter(application, voter, comparedFields) {
    var _a;
    var data = applicantData(application);
    var joint = JOINT_NAME_DOCUMENTS.includes((_a = data[ID_CARD_TYPE_FIELD]) !== null && _a !== void 0 ? _a : "") &&
        comparedFields.includes(FIRST_NAME);
    var details = [];
    comparedFields.forEach(function (field) {
        if (joint && field === MIDDLE_NAME) {
            return;
        }
        var fields = joint && field === FIRST_NAME ? [FIRST_NAME, MIDDLE_NAME] : [field];
        var enrollment = fields
            .map(function (name) { var _a; return (_a = data[name]) !== null && _a !== void 0 ? _a : ""; })
            .join(" ")
            .trim();
        var registry = fields
            .map(function (name) { return voterValue(voter, name); })
            .join(" ")
            .trim();
        details.push({ fields: fields, enrollment: enrollment, registry: registry, same: sameValue(enrollment, registry) });
    });
    return details;
}
/** A voter is already enrolled when an attribute that enrolling sets is set. */
export var isAlreadyEnrolled = function (voter, unsetAttributes) {
    return unsetAttributes.some(function (attribute) { return voterValue(voter, attribute) !== ""; });
};
/**
 * The registry voters compared with the enrollment, each once: the closest
 * first, and those already enrolled, who can't be chosen, last.
 */
export function rankCandidates(application, voters, comparedFields, unsetAttributes) {
    var seen = new Set();
    var candidates = [];
    voters.forEach(function (voter) {
        var _a;
        var id = (_a = voter.id) !== null && _a !== void 0 ? _a : "";
        if (!id || seen.has(id)) {
            return;
        }
        seen.add(id);
        var details = compareWithVoter(application, voter, comparedFields);
        candidates.push({
            voter: voter,
            details: details,
            matching: details.filter(function (detail) { return detail.same; }).length,
            alreadyEnrolled: isAlreadyEnrolled(voter, unsetAttributes),
        });
    });
    return candidates.sort(function (a, b) { return Number(a.alreadyEnrolled) - Number(b.alreadyEnrolled) || b.matching - a.matching; });
}
/** A value as the review shows it: a date written out, and a dash for none. */
export function displayValue(value, language) {
    if (!value) {
        return "—";
    }
    var date = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
    if (date) {
        var parsed = new Date(Date.UTC(Number(date[1]), Number(date[2]) - 1, Number(date[3])));
        if (parsed.toISOString().slice(0, 10) === value) {
            return parsed.toLocaleDateString(language, {
                day: "numeric",
                month: "short",
                year: "numeric",
                timeZone: "UTC",
            });
        }
    }
    return value;
}
var voterFilter = function (data, fields) {
    var filter = {};
    var attributes = {};
    fields.forEach(function (field) {
        var value = data[field];
        if (!value) {
            return;
        }
        var column = VOTER_COLUMNS[field];
        if (column) {
            filter[column] = { IsLike: value };
        }
        else {
            attributes[field] = value;
        }
    });
    return Object.keys(attributes).length > 0 ? __assign(__assign({}, filter), { attributes: attributes }) : filter;
};
/**
 * The registry lookups that find the voters closest to the enrollment: one
 * with every compared field, and one leaving each field out, so a voter who
 * differs in one detail is found too.
 */
export function candidateFilters(application, comparedFields) {
    var data = applicantData(application);
    var fields = comparedFields.filter(function (field) { return data[field]; });
    var lookups = __spreadArray([fields], fields.map(function (left) { return fields.filter(function (field) { return field !== left; }); }), true);
    var filters = [];
    var seen = new Set();
    lookups.forEach(function (lookup) {
        var filter = voterFilter(data, lookup);
        var key = JSON.stringify(filter);
        if (Object.keys(filter).length > 0 && !seen.has(key)) {
            seen.add(key);
            filters.push(filter);
        }
    });
    return filters;
}
/** The registry lookups of a free text search: by first name, last name and email. */
export function searchFilters(search) {
    var value = search.trim();
    if (!value) {
        return [];
    }
    var words = value.split(/\s+/);
    var filters = [
        { first_name: { IsLike: value } },
        { last_name: { IsLike: value } },
        { email: { IsLike: value } },
    ];
    if (words.length > 1) {
        filters.push({
            first_name: { IsLike: words.slice(0, -1).join(" ") },
            last_name: { IsLike: words[words.length - 1] },
        });
        filters.push({
            first_name: { IsLike: words[0] },
            last_name: { IsLike: words.slice(1).join(" ") },
        });
    }
    return filters;
}
/** "Juan Dela Cruz", or the username of a voter without a name. */
export var voterName = function (voter) {
    return [text(voter.first_name), voterValue(voter, MIDDLE_NAME), text(voter.last_name)]
        .filter(function (part) { return part; })
        .join(" ") || text(voter.username);
};
/** "the first name is “Juan Carlos” on the enrollment and “Juan” in the registry". */
export function differenceText(detail, t, fieldLabel) {
    return t("".concat(KEY, ".review.why.difference"), {
        field: joinList(detail.fields.map(function (field) { return fieldLabel(field).toLowerCase(); }), t),
        enrollment: detail.enrollment || "—",
        registry: detail.registry || "—",
    });
}
